import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/shopify-agentur";
const TITLE = "Shopify Agentur oder Freelancer: Vergleich, Kosten, Auswahl";
const DESCRIPTION =
  "Wann sich eine Shopify Agentur lohnt und wann ein Freelancer der bessere Weg ist: Leistungen, Kosten, Ablauf und Prüffragen vor der Beauftragung.";

const faqs = [
  {
    q: "Was kostet eine Shopify Agentur?",
    a: "Übliche Stundensätze liegen im DACH-Raum bei 90 bis 180 € netto, dazu kommen häufig Mindestbudgets pro Projekt oder Monat. Bei mir sind es 110 € netto pro Stunde ohne Mindestumfang, größere Vorhaben zum Festpreis.",
  },
  {
    q: "Agentur oder Freelancer, was ist besser?",
    a: "Das hängt vom Umfang ab. Wenn parallel Design, Entwicklung, Content und Werbung laufen sollen, ist ein Team sinnvoll. Wenn es um Shopaufbau, Theme-Entwicklung oder Conversion geht, arbeiten Sie mit einem Freelancer direkt mit der Person, die umsetzt.",
  },
  {
    q: "Was sollte im Angebot stehen?",
    a: "Der konkrete Umfang, wer die Arbeit ausführt, wie abgerechnet wird, wie lange es dauert und was passiert, wenn sich der Umfang ändert. Ein Angebot ohne diese Punkte lässt sich nicht vergleichen.",
  },
  {
    q: "Kann ein Freelancer einen kompletten Shop aufbauen?",
    a: "Ja. Theme-Aufbau, Produkte, Zahlung, Versand, Steuern und Rechtstexte lassen sich als abgeschlossenes Projekt umsetzen. Danach pflegen Sie den Shop selbst weiter oder buchen Stunden nach Bedarf.",
  },
  {
    q: "Was ist bei laufender Betreuung sinnvoll?",
    a: "Ein fester Ansprechpartner und Abrechnung nach tatsächlichem Aufwand. Viele buchen ein paar Stunden im Monat für alles, was so aufläuft, ohne Vertragsbindung.",
  },
];

export const Route = createFileRoute("/shopify-agentur")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({ title: TITLE, description: DESCRIPTION, path: PATH, faqs }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Orientierung · Zusammenarbeit"
      title="Shopify Agentur oder Freelancer: was zu Ihrem Vorhaben passt"
      intro="Beide Wege führen zu einem funktionierenden Shop, sie unterscheiden sich in Struktur, Kosten und Nähe zur Umsetzung. Diese Übersicht zeigt, wann welcher Weg sinnvoll ist und woran Sie ein belastbares Angebot erkennen."
      source="shopify-agentur"
      sections={[
        {
          h: "Der eigentliche Unterschied",
          p: [
            "Ein Team verteilt Aufgaben auf mehrere Rollen: Projektleitung, Design, Entwicklung, teilweise Werbung. Bei einem Freelancer sprechen Sie direkt mit der Person, die im Theme arbeitet.",
            "Das ist kein Qualitätsurteil, sondern eine Frage des Umfangs. Bei vielen parallelen Aufgaben hilft Struktur, bei klar umrissenen Vorhaben kostet sie vor allem Zeit und Geld.",
          ],
        },
        {
          h: "Kosten realistisch einschätzen",
          p: [
            "Stundensätze liegen im DACH-Raum meist zwischen 90 und 180 € netto. Wichtiger als der Satz ist, wie viele Stunden tatsächlich in Ihrem Shop landen und wie viele in Abstimmung.",
            "Bei mir: 110 € netto pro Stunde für Anpassungen, Festpreis nach Umfang für Shopaufbau, Conversion-Optimierung ab 3.990 € netto.",
          ],
        },
        {
          h: "Wann ein Team sinnvoll ist",
          p: [],
          list: [
            "Mehrere Länder, Sprachen oder Shops gleichzeitig",
            "Design, Content, Entwicklung und Werbung laufen parallel",
            "Feste Ansprechzeiten und Vertretung sind vertraglich nötig",
          ],
        },
        {
          h: "Wann ein Freelancer der kürzere Weg ist",
          p: [],
          list: [
            "Ein neuer Shop soll sauber aufgebaut und übergeben werden",
            "Eine konkrete Anpassung, Funktion oder ein Fehler steht an",
            "Der Shop läuft, soll aber pro Besucher mehr verkaufen",
            "Kleinere Aufgaben sollen nicht an einem Mindestbudget scheitern",
          ],
        },
        {
          h: "Fragen, die Angebote vergleichbar machen",
          p: [],
          list: [
            "Wer arbeitet tatsächlich im Shop?",
            "Was genau ist im Preis enthalten, was nicht?",
            "Wie wird gearbeitet, in einer Theme-Kopie oder im laufenden Shop?",
            "Wie lange dauert es und wovon hängt der Termin ab?",
            "Was passiert nach dem Livegang mit Fehlern und Anpassungen?",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-freelancer", label: "Shopify Freelancer" },
        { to: "/ratgeber/shopify-agentur-kosten", label: "Kosten im Detail" },
        { to: "/shopify-entwickler", label: "Shopify Entwickler buchen" },
        { to: "/fragen/shopify-agentur-oder-freelancer", label: "Agentur oder Freelancer?" },
      ]}
    />
  );
}
